const STORAGE_KEY = 'notification_center'

const notificationTypes = {
  MESSAGE: 'message',
  NOTICE: 'notice', 
  ALERT: 'alert'
}

const notificationPriorities = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  URGENT: 'urgent'
}

export default {
  namespaced: true,
  
  state: {
    messages: [],
    notices: [],
    alerts: [],
    unreadCount: {
      message: 0,
      notice: 0,
      alert: 0,
      total: 0
    },
    isConnected: false,
    lastConnectedTime: null,
    settings: {
      soundEnabled: true,
      desktopNotificationEnabled: true,
      autoDismiss: {
        message: 5000,
        notice: 10000,
        alert: 0
      }
    }
  },

  getters: {
    allNotifications: (state) => {
      return [
        ...state.messages.map(m => ({...m, type: notificationTypes.MESSAGE})),
        ...state.notices.map(n => ({...n, type: notificationTypes.NOTICE})),
        ...state.alerts.map(a => ({...a, type: notificationTypes.ALERT}))
      ].sort((a, b) => new Date(b.createTime) - new Date(a.createTime))
    },
    
    unreadTotal: (state) => state.unreadCount.total,
    
    getTypeList: (state) => (type) => {
      switch(type) {
        case notificationTypes.MESSAGE: return state.messages
        case notificationTypes.NOTICE: return state.notices
        case notificationTypes.ALERT: return state.alerts
        default: return []
      }
    },
    
    getUnreadByType: (state) => (type) => {
      return state.unreadCount[type] || 0
    }
  },

  mutations: {
    SET_CONNECTED(state, isConnected) {
      state.isConnected = isConnected
      if (isConnected) {
        state.lastConnectedTime = new Date().toISOString()
      }
    },

    ADD_NOTIFICATION(state, notification) {
      const { type, id } = notification
      const list = this.getTypeListByType(type, state)
      
      if (!list.find(n => n.id === id)) {
        list.unshift({
          ...notification,
          read: false,
          createTime: notification.createTime || new Date().toISOString()
        })
        
        if (!notification.read) {
          state.unreadCount[type]++
          state.unreadCount.total++
        }
        
        if (list.length > 100) {
          list.pop()
        }
        
        this.saveToStorage(state)
      }
    },

    MARK_READ(state, { type, id }) {
      const list = this.getTypeListByType(type, state)
      const item = list.find(n => n.id === id)
      if (item && !item.read) {
        item.read = true
        state.unreadCount[type]--
        state.unreadCount.total--
        this.saveToStorage(state)
      }
    },

    MARK_ALL_READ(state, type) {
      if (type) {
        const list = this.getTypeListByType(type, state)
        list.forEach(item => {
          if (!item.read) {
            item.read = true
            state.unreadCount[type]--
            state.unreadCount.total--
          }
        })
      } else {
        Object.values(notificationTypes).forEach(t => {
          const list = this.getTypeListByType(t, state)
          list.forEach(item => {
            if (!item.read) {
              item.read = true
              state.unreadCount[t]--
              state.unreadCount.total--
            }
          })
        })
      }
      this.saveToStorage(state)
    },

    CLEAR_TYPE(state, type) {
      const list = this.getTypeListByType(type, state)
      state.unreadCount[type] = 0
      state.unreadCount.total = state.unreadCount.message + state.unreadCount.notice + state.unreadCount.alert
      list.splice(0, list.length)
      this.saveToStorage(state)
    },

    CLEAR_ALL(state) {
      state.messages = []
      state.notices = []
      state.alerts = []
      state.unreadCount = { message: 0, notice: 0, alert: 0, total: 0 }
      this.saveToStorage(state)
    },

    REMOVE_NOTIFICATION(state, { type, id }) {
      const list = this.getTypeListByType(type, state)
      const index = list.findIndex(n => n.id === id)
      if (index > -1) {
        const item = list[index]
        if (!item.read) {
          state.unreadCount[type]--
          state.unreadCount.total--
        }
        list.splice(index, 1)
        this.saveToStorage(state)
      }
    },

    UPDATE_SETTINGS(state, settings) {
      state.settings = { ...state.settings, ...settings }
      this.saveToStorage(state)
    },

    SET_UNREAD_COUNT(state, { total }) {
      if (total !== undefined) {
        state.unreadCount.total = total
      }
    },

    LOAD_FROM_STORAGE(state) {
      try {
        const data = localStorage.getItem(STORAGE_KEY)
        if (data) {
          const parsed = JSON.parse(data)
          state.messages = parsed.messages || []
          state.notices = parsed.notices || []
          state.alerts = parsed.alerts || []
          state.unreadCount = parsed.unreadCount || { message: 0, notice: 0, alert: 0, total: 0 }
          state.settings = { ...state.settings, ...parsed.settings }
        }
      } catch (e) {
        console.error('Failed to load notifications from storage:', e)
      }
    }
  },

  actions: {
    init({ commit }) {
      commit('LOAD_FROM_STORAGE')
    },

    addNotification({ commit, state, rootState }, notification) {
      commit('ADD_NOTIFICATION', notification)
      
      if (state.settings.soundEnabled && notification.type === notificationTypes.ALERT) {
        this.playAlertSound()
      }
      
      if (state.settings.desktopNotificationEnabled) {
        this.showDesktopNotification(notification)
      }
    },

    markRead({ commit }, payload) {
      commit('MARK_READ', payload)
    },

    markAllRead({ commit }, type) {
      commit('MARK_ALL_READ', type)
    },

    clearType({ commit }, type) {
      commit('CLEAR_TYPE', type)
    },

    clearAll({ commit }) {
      commit('CLEAR_ALL')
    },

    remove({ commit }, payload) {
      commit('REMOVE_NOTIFICATION', payload)
    },

    updateSettings({ commit }, settings) {
      commit('UPDATE_SETTINGS', settings)
    }
  },

  getTypeListByType(type, state) {
    switch(type) {
      case notificationTypes.MESSAGE: return state.messages
      case notificationTypes.NOTICE: return state.notices
      case notificationTypes.ALERT: return state.alerts
      default: return []
    }
  },

  saveToStorage(state) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        messages: state.messages,
        notices: state.notices,
        alerts: state.alerts,
        unreadCount: state.unreadCount,
        settings: state.settings
      }))
    } catch (e) {
      console.error('Failed to save notifications to storage:', e)
    }
  },

  playAlertSound() {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)()
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()
      
      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)
      
      oscillator.frequency.value = 800
      oscillator.type = 'sine'
      gainNode.gain.value = 0.3
      
      oscillator.start()
      setTimeout(() => oscillator.stop(), 200)
    } catch (e) {
    }
  },

  showDesktopNotification(notification) {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(notification.title, {
        body: notification.text || notification.content || '',
        icon: '/favicon.ico'
      })
    } else if ('Notification' in window && Notification.permission !== 'denied') {
      Notification.requestPermission()
    }
  }
}

export { notificationTypes, notificationPriorities }
